import * as React from 'react';
import master from '../../Master';
import { Checkbox, Input } from '../Input';
import {
  getAreaDisplayUnitLabel,
  toDisplayEverguardThreshold,
  toStoredEverguardThreshold,
} from '../../utils/PlaceCodeDisplayUnits';

/** 剥落防止の設定に使う自動計測項目（浮き面積）のid */
const EVERGUARD_INPUT_FIELD_ID = 5;

/** 剥落防止の設定を有効にしたときに、浮き面積の基準として最初に入れておく値（㎡） */
const DEFAULT_EVERGUARD_THRESHOLD = 0.5;

/** 図形の種類が浮き面積を計測する（剥落防止の設定ができる）か */
export function shapeUsesEverguard(shapeSchemeId: number): boolean {
  const shape = master.Shapes.find((shapeData) => shapeData.id === shapeSchemeId);
  if (!shape) {
    return false;
  }
  return shape.input_field_ids.some((fieldId) => fieldId === EVERGUARD_INPUT_FIELD_ID);
}

/**
 * 保存済みの浮き面積の基準（㎡）を、面積の表示単位に換算して入力欄に出す文字列にする。
 * 設定がないときは剥落防止の設定を無効として扱う
 */
export function toEverguardThresholdText(
  threshold: number | undefined,
  areaDisplayUnit: number
): string | undefined {
  if (threshold === undefined) {
    return undefined;
  }
  return toDisplayEverguardThreshold(threshold, areaDisplayUnit).toString();
}

/**
 * 入力欄の文字列（面積の表示単位の値）を、保存する浮き面積の基準（㎡）にする。
 * 0より大きい数値でないときは値がないものとして扱う
 */
export function toStoredEverguardThresholdFromText(
  text: string | undefined,
  areaDisplayUnit: number
): number | undefined {
  const displayThreshold = parseEverguardThreshold(text);
  if (displayThreshold === undefined) {
    return undefined;
  }
  return toStoredEverguardThreshold(displayThreshold, areaDisplayUnit);
}

/**
 * 面積の表示単位を変えたときに、入力欄の文字列を新しい表示単位の値に換算する。
 * 入力欄が数値として読めないときは、利用者の入力をそのまま残して誤りを見せ続ける
 */
export function convertEverguardThresholdText(
  text: string | undefined,
  fromAreaDisplayUnit: number,
  toAreaDisplayUnit: number
): string | undefined {
  const storedThreshold = toStoredEverguardThresholdFromText(text, fromAreaDisplayUnit);
  if (storedThreshold === undefined) {
    return text;
  }
  return toEverguardThresholdText(storedThreshold, toAreaDisplayUnit);
}

/** 入力欄の文字列を浮き面積の基準の数値にする。0より大きい数値でないときは値がないものとして扱う */
export function parseEverguardThreshold(text: string | undefined): number | undefined {
  if (text === undefined || text.trim() === '') {
    return undefined;
  }
  const value = Number(text);
  if (!isFinite(value) || value <= 0) {
    return undefined;
  }
  return value;
}

/** 剥落防止の設定が有効なときに、浮き面積の基準の入力の誤りを返す */
export function getEverguardThresholdError(text: string | undefined): string | undefined {
  if (text === undefined) {
    return undefined;
  }
  if (text.trim() === '') {
    return '必須入力項目です';
  }
  if (parseEverguardThreshold(text) === undefined) {
    return '0より大きい数値を入力してください';
  }
  return undefined;
}

interface PlaceCodeEverguardInputProps {
  shapeSchemeId: number;
  /** 面積の表示単位。浮き面積の基準はこの単位で入力する */
  areaDisplayUnit: number;
  /** 浮き面積の基準の入力欄の文字列（面積の表示単位の値）。undefinedのときは剥落防止の設定が無効 */
  thresholdText: string | undefined;
  validationMessage: string | undefined;
  onChange: (thresholdText: string | undefined) => void;
}

export const PlaceCodeEverguardInput: React.FC<PlaceCodeEverguardInputProps> = function (props) {
  if (!shapeUsesEverguard(props.shapeSchemeId)) {
    return null;
  }

  const useEverguard = props.thresholdText !== undefined;

  return (
    <div className="App-input_container">
      <label className="PlaceCreateCodePage-checkbox_label">
        <Checkbox
          value={useEverguard}
          onChange={(checked) => {
            if (checked) {
              props.onChange(toEverguardThresholdText(DEFAULT_EVERGUARD_THRESHOLD, props.areaDisplayUnit));
            } else {
              props.onChange(undefined);
            }
          }}
        />
        剥落防止用の補修コード
      </label>
      {(() => {
        if (!useEverguard) {
          return null;
        }
        return (
          <div className="PlaceCreateCodePage-everguard_threshold">
            <label className="App-label" htmlFor="PlaceCreateCodePage-everguard_threshold">
              浮き面積設定
              <span className="App-label_required" aria-hidden="true">*</span>
            </label>
            <div className="PlaceCreateCodePage-everguard_threshold_row">
              <Input
                id="PlaceCreateCodePage-everguard_threshold"
                type="number"
                min={0}
                step={0.1}
                required={true}
                value={props.thresholdText}
                validationMessage={props.validationMessage}
                onChange={(event) => props.onChange(event.target.value)}
              />
              <span>{getAreaDisplayUnitLabel(props.areaDisplayUnit)} 以上</span>
            </div>
          </div>
        );
      })()}
    </div>
  );
};
